import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Shield, Users, Plus, Edit, Trash2, Check, ChevronDown, ChevronRight, Save, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Roles = () => {
  const [roles, setRoles] = useState([]);
  const [permissionsByModule, setPermissionsByModule] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [expandedModules, setExpandedModules] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: []
  });

  useEffect(() => {
    fetchRoles();
    fetchPermissions();
  }, []);

  const fetchRoles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/roles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRoles(response.data);
    } catch (error) {
      toast.error('Error al cargar roles');
    } finally {
      setLoading(false);
    }
  };

  const fetchPermissions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/permissions/by-module`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPermissionsByModule(response.data);
      
      const expanded = {};
      response.data.forEach(mod => {
        expanded[mod.module] = true;
      });
      setExpandedModules(expanded);
    } catch (error) {
      console.error('Error al cargar permisos');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('El nombre del rol es obligatorio');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      if (editingRole) {
        await axios.put(`${API}/roles/${editingRole.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Rol actualizado correctamente');
      } else {
        await axios.post(`${API}/roles`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Rol creado correctamente');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar rol');
    }
  };

  const handleEdit = (role) => {
    setEditingRole(role);
    setFormData({
      name: role.name,
      description: role.description || '',
      permissions: role.permissions || []
    });
    setDialogOpen(true);
  };

  const handleDelete = async (roleId) => {
    if (!window.confirm('¿Estás seguro de eliminar este rol?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/roles/${roleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Rol eliminado correctamente');
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar rol');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      permissions: []
    });
    setEditingRole(null);
  };

  const toggleModule = (moduleName) => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleName]: !prev[moduleName]
    }));
  };

  const togglePermission = (code) => {
    setFormData(prev => {
      const newPerms = prev.permissions.includes(code)
        ? prev.permissions.filter(p => p !== code)
        : [...prev.permissions, code];
      return { ...prev, permissions: newPerms };
    });
  };

  const toggleAllModulePermissions = (moduleName, modulePermissions) => {
    const moduleCodes = modulePermissions.map(p => p.code);
    const allSelected = moduleCodes.every(c => formData.permissions.includes(c));
    
    setFormData(prev => {
      let newPerms;
      if (allSelected) {
        newPerms = prev.permissions.filter(p => !moduleCodes.includes(p));
      } else {
        newPerms = [...new Set([...prev.permissions, ...moduleCodes])];
      }
      return { ...prev, permissions: newPerms };
    });
  };

  const getModuleName = (module) => {
    const names = {
      'stores': 'Tiendas',
      'transactions': 'Transacciones',
      'banks': 'Bancos',
      'reports': 'Reportes',
      'budgets': 'Presupuestos',
      'users': 'Usuarios',
      'roles': 'Roles',
      'settings': 'Configuracion',
      'assets': 'Activos',
      'loans': 'Prestamos',
      'suppliers': 'Proveedores'
    };
    return names[module] || module.charAt(0).toUpperCase() + module.slice(1);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="roles-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Shield className="h-8 w-8 text-indigo-600" />
            Roles y Permisos
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            Gestiona los roles del sistema y sus permisos asociados
          </p>
        </div>
        <Button 
          onClick={() => {
            resetForm();
            setDialogOpen(true);
          }}
          data-testid="add-role-button"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nuevo Rol
        </Button>
      </div>

      {/* Grid de Roles */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {roles.map(role => (
          <Card 
            key={role.id} 
            className={`hover:shadow-md transition-all ${
              role.is_system_role 
                ? 'border-indigo-200 dark:border-indigo-800 bg-indigo-50/50 dark:bg-indigo-900/20' 
                : ''
            }`}
            data-testid={`role-card-${role.name.toLowerCase()}`}
          >
            <CardHeader className="pb-3">
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${
                    role.is_system_role 
                      ? 'bg-indigo-100 dark:bg-indigo-900' 
                      : 'bg-slate-100 dark:bg-slate-800'
                  }`}>
                    {role.is_system_role 
                      ? <Lock className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                      : <Users className="h-5 w-5 text-slate-600 dark:text-slate-400" />
                    }
                  </div>
                  <div>
                    <CardTitle className="text-lg">
                      {role.name}
                    </CardTitle>
                    {role.is_system_role && (
                      <span className="text-xs text-indigo-600 dark:text-indigo-400 font-medium">
                        Rol del Sistema
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEdit(role)}
                    className="h-8 w-8 p-0"
                    data-testid={`edit-role-${role.name.toLowerCase()}`}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  {!role.is_system_role && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(role.id)}
                      className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-role-${role.name.toLowerCase()}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                {role.description || 'Sin descripcion'}
              </p>
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  {role.permissions?.length || 0} permisos asignados
                </span>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                  role.is_system_role
                    ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300'
                    : 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300'
                }`}>
                  {role.is_system_role ? 'Sistema' : 'Personalizado'}
                </span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Dialog para crear/editar rol */}
      <Dialog open={dialogOpen} onOpenChange={(open) => {
        setDialogOpen(open);
        if (!open) resetForm();
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              {editingRole ? 'Editar Rol' : 'Nuevo Rol'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Información básica */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nombre del Rol *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ej: Supervisor"
                  disabled={editingRole?.is_system_role}
                  data-testid="role-name-input"
                />
                {editingRole?.is_system_role && (
                  <p className="text-xs text-amber-600">
                    Los roles del sistema no pueden cambiar de nombre
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Descripcion</Label>
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descripcion del rol"
                  data-testid="role-description-input"
                />
              </div>
            </div>

            {/* Selector de Permisos */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Permisos Asignados</Label>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Selecciona los permisos que tendra este rol. 
                <span className="font-medium ml-1 text-indigo-600">
                  {formData.permissions.length} permisos seleccionados
                </span>
              </p>
              
              <div className="border rounded-lg divide-y dark:border-slate-700 dark:divide-slate-700">
                {permissionsByModule.map(module => {
                  const moduleCodes = module.permissions.map(p => p.code);
                  const selectedCount = moduleCodes.filter(c => formData.permissions.includes(c)).length;
                  const allSelected = selectedCount === moduleCodes.length;
                  
                  return (
                    <div key={module.module}>
                      {/* Header del módulo */}
                      <div 
                        className="px-4 py-3 bg-slate-50 dark:bg-slate-800 flex items-center justify-between cursor-pointer"
                        onClick={() => toggleModule(module.module)}
                      >
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={allSelected}
                            onChange={() => toggleAllModulePermissions(module.module, module.permissions)}
                            onClick={(e) => e.stopPropagation()}
                            className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <span className="font-semibold text-slate-800 dark:text-white">
                            {getModuleName(module.module)}
                          </span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            selectedCount > 0 
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
                              : 'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-400'
                          }`}>
                            {selectedCount}/{moduleCodes.length}
                          </span>
                          {expandedModules[module.module] 
                            ? <ChevronDown className="h-4 w-4 text-slate-500" />
                            : <ChevronRight className="h-4 w-4 text-slate-500" />
                          }
                        </div>
                      </div>
                      
                      {/* Lista de permisos */}
                      {expandedModules[module.module] && (
                        <div className="px-4 py-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                          {module.permissions.map(perm => (
                            <label 
                              key={perm.code}
                              className={`flex items-center gap-3 p-2 rounded-lg border transition-all cursor-pointer ${
                                formData.permissions.includes(perm.code)
                                  ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800'
                                  : 'bg-white border-slate-200 hover:bg-slate-50 dark:bg-slate-900 dark:border-slate-700 dark:hover:bg-slate-800'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={formData.permissions.includes(perm.code)}
                                onChange={() => togglePermission(perm.code)}
                                className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-sm text-slate-800 dark:text-white truncate">
                                  {perm.name}
                                </div>
                                <div className="text-xs text-slate-500 dark:text-slate-400 truncate">
                                  {perm.description}
                                </div>
                              </div>
                              {formData.permissions.includes(perm.code) && (
                                <Check className="h-4 w-4 text-green-600 dark:text-green-400 flex-shrink-0" />
                              )}
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Botones */}
            <div className="flex justify-end gap-3 pt-4 border-t dark:border-slate-700">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setDialogOpen(false)}
              >
                Cancelar
              </Button>
              <Button type="submit" data-testid="save-role-button">
                <Save className="h-4 w-4 mr-2" />
                {editingRole ? 'Guardar Cambios' : 'Crear Rol'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Roles;
